import * as React from 'react';

import { FileTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WhatsAppFlowContent } from '@/components/organisms/Dialogs/Action/Flow/WhatsAppFlow';
import { QuestionSetContent } from '@/components/organisms/Dialogs/Action/QuestionSet/QuestionSet';
import { Carousel } from '@/components/organisms/Dialogs/Carousel/Carousel';
import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import { cn } from '@/lib/utils';
import { CarouselCard, QuestionSetConfig, WhatsAppFlowDisplay } from '@/shared/types/action';
import { ConversationSource, Attachment as AttachmentType, AuthorType } from '@/shared/types/chat';
import { switchChannelName } from '@/util/switchChannel';
import { splitArray, splitFileName } from '@/util/util';

import { Attachment } from '../Attachment/Attachment';
import Attachments from '../Attachment/Attachments';
import { ImageStack } from '../Attachment/ImageStack';

interface ExtendedProps {
  type?: string;
  text?: string;
  url?: string;
  id?: string;
  name?: string;
  action_id?: string;
  cards?: CarouselCard[];
  label?: string;
  options?: { text: string; label: string }[];
  attachments?: AttachmentType[];
  hasAvatar?: boolean;
  source?: ConversationSource;
  authorType?: `${AuthorType}`;
  handleAttachmentError?: (error?: Error) => void;
  whatsapp_flow?: WhatsAppFlowDisplay;
  question_set?: QuestionSetConfig;
  channel?: string;
  integration_id?: string;
  expires_at?: string;
}

const FileCard = ({ fileName, url }: { fileName: string; url: string }) => {
  const { baseName, ext } = splitFileName(fileName);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex w-60 items-center gap-2 rounded-xl bg-card p-2 shadow-md transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-selected-soft">
        <FileTextIcon size={20} className="text-muted-foreground" />
      </div>
      <div className="flex min-w-0 flex-col overflow-hidden">
        <span className="truncate text-sm leading-5 font-medium text-foreground">{baseName}</span>
        <span className="text-xs leading-4 font-normal text-muted-foreground uppercase">{ext}</span>
      </div>
    </a>
  );
};

// Common bubble styles
const bubbleBase =
  'mb-0 mt-0 min-w-10.25 max-w-lg w-fit overflow-hidden break-words rounded-lg px-4 py-1.75 text-sm font-normal leading-5 text-primary-foreground';

const bubbleBrainText =
  'ml-auto bg-primary [&>p]:m-0 [&>p]:mb-1 [&>p]:leading-5 [&>p:last-child]:mb-0 [&_a]:underline [&>h1]:text-[32px] [&>h1]:leading-10 [&>h1]:font-semibold [&>h1]:mb-2 [&>h2]:text-[22px] [&>h2]:leading-7 [&>h2]:font-semibold [&>h2]:mb-1.5 [&>h3]:text-lg [&>h3]:leading-6 [&>h3]:font-semibold [&>h3]:mb-1 [&>h4]:text-base [&>h4]:leading-6 [&>h4]:font-semibold [&>h4]:mb-1 [&>h5]:text-sm [&>h5]:leading-5 [&>h5]:font-semibold [&>h5]:mb-0.5 [&>h6]:text-xs [&>h6]:leading-4 [&>h6]:font-semibold [&>h6]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:border-primary-foreground/40 [&_blockquote]:pl-3 [&_blockquote]:my-1';

const bubbleWithAvatar = 'rounded-br-none';

const bubbleBrainUrl =
  'ml-auto bg-primary [&_a]:flex [&_a]:items-center [&_a]:gap-1 [&_a]:text-primary-foreground [&_a]:underline';

export const Response = ({
  ref,
  ...props
}: ExtendedProps & { ref?: React.Ref<HTMLSpanElement | HTMLDivElement> }) => {
  const { t } = useTranslation();
  const { html } = useMarkdownToHtml(props.text || '');
  // Destructure known props and capture the remaining as rest.
  const {
    type,
    attachments,
    text,
    url,
    id,
    name,
    cards,
    options,
    label,
    hasAvatar,
    source,
    authorType,
    handleAttachmentError,
    whatsapp_flow,
    question_set,
    // switch_channel fields, kept off the DOM
    channel,
    integration_id: _integrationId,
    expires_at: _expiresAt,
    ...rest
  } = props;

  if (attachments?.length > 0) {
    const allImages = attachments.every((a) => a.mime_type?.includes('image'));
    const allVideos = attachments.every((a) => a.mime_type?.includes('video'));
    const singleAudio = attachments.length === 1 && attachments[0].mime_type?.includes('audio');
    const singleFile =
      attachments.length === 1 &&
      attachments[0].type === 'file' &&
      !attachments[0].mime_type?.includes('image') &&
      !attachments[0].mime_type?.includes('video') &&
      !attachments[0].mime_type?.includes('audio');

    if (allImages || allVideos || singleFile || singleAudio) {
      let attachmentChild: React.ReactNode;
      if ((allImages || allVideos) && attachments.length > 1) {
        attachmentChild = (
          <ImageStack
            attachments={attachments}
            source={source}
            authorType={authorType}
            handleError={handleAttachmentError}
          />
        );
      } else if (singleAudio) {
        attachmentChild = (
          <div className="w-fit rounded-full bg-card p-2 shadow-md">
            <Attachment
              attachment={attachments[0]}
              source={source}
              authorType={authorType}
              handleError={handleAttachmentError}
            />
          </div>
        );
      } else {
        attachmentChild = (
          <Attachment
            attachment={attachments[0]}
            source={source}
            authorType={authorType}
            handleError={handleAttachmentError}
            standalone
          />
        );
      }

      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className="ml-auto flex flex-col items-end gap-2"
        >
          {text && (
            <div
              className={cn(bubbleBase, bubbleBrainText)}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {attachmentChild}
        </div>
      );
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        {...rest}
        className={cn(bubbleBase, bubbleBrainText, hasAvatar && bubbleWithAvatar)}
      >
        <div className={text ? 'mb-3' : ''}>
          {splitArray(attachments, 4).map((chunk, chunkIndex) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={chunkIndex}
              className={cn(text && 'flex justify-center')}
            >
              <Attachments>
                {chunk.map((attachment) => (
                  <Attachment
                    key={attachment?.id ?? attachment.url}
                    attachment={attachment}
                    source={source}
                    isSingleItem={attachments.length === 1}
                    authorType={authorType}
                    bg="dark"
                    handleError={handleAttachmentError}
                  />
                ))}
              </Attachments>
            </div>
          ))}
        </div>
        {text && (
          <div className="float-right text-sm leading-5 font-normal text-primary-foreground">
            <span dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        )}
      </div>
    );
  }

  switch (type) {
    case 'switch_channel':
      return (
        <p
          ref={ref as React.Ref<HTMLParagraphElement>}
          {...rest}
          className={cn(bubbleBase, bubbleBrainUrl)}
        >
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {t('conversation.switch_channel_sent', { 0: switchChannelName(t, channel) })}
            </a>
          ) : (
            t('conversation.switch_channel_sent', { 0: switchChannelName(t, channel) })
          )}
        </p>
      );
    case 'url':
      return (
        <p
          ref={ref as React.Ref<HTMLParagraphElement>}
          {...rest}
          className={cn(bubbleBase, bubbleBrainUrl)}
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </p>
      );
    case 'audio':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className="ml-auto w-fit rounded-full bg-card p-2 shadow-md"
        >
          <audio
            src={url}
            controls
            onError={() =>
              handleAttachmentError(new Error(t('conversation.attachment_error_audio')))
            }
          />
        </div>
      );
    case 'image':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className="ml-auto flex flex-col gap-1.5"
        >
          <Attachment
            source={source}
            attachment={
              id
                ? { id, type: 'image', mime_type: 'image/jpeg' }
                : { url, type: 'image', mime_type: 'image/jpeg' }
            }
            authorType={authorType}
            handleError={handleAttachmentError}
            standalone
          />
          {name && <span className="text-right text-xs text-muted-foreground">{name}</span>}
        </div>
      );
    case 'video':
      if (id) {
        return (
          <div ref={ref as React.Ref<HTMLDivElement>} {...rest} className="ml-auto">
            <Attachment
              source={source}
              attachment={{ id, type: 'video', mime_type: 'video/mp4' }}
              authorType={authorType}
              handleError={handleAttachmentError}
              standalone
            />
          </div>
        );
      }
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className="ml-auto rounded-xl bg-card p-1.5 shadow-md transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg"
        >
          <video
            src={url}
            controls
            className="block max-h-72 max-w-72 rounded-lg"
            onError={() =>
              handleAttachmentError(new Error(t('conversation.attachment_error_video')))
            }
          />
        </div>
      );
    case 'file':
      if (id) {
        return (
          <div ref={ref as React.Ref<HTMLDivElement>} {...rest} className="ml-auto">
            <Attachment
              source={source}
              attachment={{ id, type: 'file', mime_type: '' }}
              authorType={authorType}
              handleError={handleAttachmentError}
              standalone
            />
          </div>
        );
      }
      return (
        <div ref={ref as React.Ref<HTMLDivElement>} {...rest} className="ml-auto">
          <FileCard fileName={name || text || ''} url={url} />
        </div>
      );
    case 'carousel':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className={cn('mt-2', cards?.length === 1 ? 'w-60' : 'w-70')}
        >
          <Carousel cards={cards} />
        </div>
      );
    case 'webview':
    case 'survey':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className="h-30 w-55 rounded bg-background shadow-sm [&_p]:px-3 [&_p]:py-2 [&_p]:text-ellipsis"
        >
          <p>{name}</p>
          <div className="flex h-[calc(100%-50px)] w-full cursor-not-allowed items-center justify-center rounded-br rounded-bl bg-muted/50 text-muted-foreground">
            <p className="w-4/5 rounded bg-background px-4 py-2 text-center text-sm leading-5 font-semibold text-ellipsis">
              {label}
            </p>
          </div>
        </div>
      );
    case 'whatsapp_flow':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className={cn(bubbleBase, bubbleBrainText, hasAvatar && bubbleWithAvatar)}
        >
          <WhatsAppFlowContent flow={whatsapp_flow} />
        </div>
      );
    case 'question_set':
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...rest}
          className={cn(bubbleBase, bubbleBrainText, hasAvatar && bubbleWithAvatar)}
        >
          <QuestionSetContent questionSet={question_set} />
        </div>
      );
    default:
      return (
        <>
          <div
            ref={ref as React.Ref<HTMLDivElement>}
            {...rest}
            className={cn(bubbleBase, bubbleBrainText, hasAvatar && bubbleWithAvatar)}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {options && (
            <div className="text-right">
              {options.map((option) => (
                <button
                  key={`option-${option.text}`}
                  className="mx-0.5 mb-2 inline-block h-6.5 cursor-not-allowed rounded-lg border border-muted bg-muted/50 px-2 py-1 text-xs leading-4 font-normal first:mt-1 last:mr-0"
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </>
      );
  }
};
